import { InfoIcon } from '@phosphor-icons/react';
import {
  Avatar,
  Badge,
  Button,
  Cell,
  Row,
  TextAreaField,
  TextField,
  Tooltip,
} from '@skupreme/shared/ui';
import { FormItem } from '@skupreme/shared/ui/form';
import { tooltipTexts } from '@utility/constants/common';
import { conflictHelpText } from '@utility/constants/strings';
import { type FormEvent, useEffect, useState } from 'react';
import { MarketplaceIcons } from '@features/catalog';
import { flagImages } from '../legacy-bridge';
import {
  type ConflictErrors,
  type ConflictValues,
  initialValues,
  validate,
} from '../lib/conflicts';
import type { ConflictAttribute, ConflictItem } from '../lib/types';

export type ConflictRowProps = {
  item: ConflictItem;
  region?: string;
  /** What stands in for a UPC that is not applicable: ASIN, or Shopify's item id. */
  alternateIdentifier: string;
  onResolve: (
    item: ConflictItem,
    values: ConflictValues,
    notApplicable: boolean,
  ) => Promise<void>;
  isReadOnly?: boolean;
  isActionsHidden?: boolean;
};

const SHORT_TITLE_MAX = 75;
const DISCONTINUED = ['inactive', 'discontinued'];

function hintFor(fieldName: string) {
  switch (fieldName.toLowerCase()) {
    case 'upc':
      return tooltipTexts.UPC;
    case 'gtin':
      return tooltipTexts.GTIN;
    case 'upc/gtin':
      return `${tooltipTexts.UPC} ${tooltipTexts.GTIN}`;
    default:
      return undefined;
  }
}

/** One catalog item and the attributes it still needs, filled in place. */
export function ConflictRow({
  item,
  region,
  alternateIdentifier,
  onResolve,
  isReadOnly,
  isActionsHidden,
}: ConflictRowProps) {
  const [values, setValues] = useState<ConflictValues>(() =>
    initialValues(item),
  );
  const [errors, setErrors] = useState<ConflictErrors>({});
  const [pending, setPending] = useState<'resolve' | 'ignore' | null>(null);

  useEffect(() => {
    setValues(initialValues(item));
    setErrors({});
  }, [item]);

  const change = (field: string, value: string) => {
    const next = { ...values, [field]: value };
    setValues(next);
    setErrors(validate(next).errors);
  };

  const submit = async (notApplicable: boolean) => {
    if (!notApplicable) {
      const result = validate(values);
      setErrors(result.errors);
      if (!result.isValid) return;
    }

    try {
      setPending(notApplicable ? 'ignore' : 'resolve');
      await onResolve(item, values, notApplicable);
    } finally {
      setPending(null);
    }
  };

  const flag = region ? flagImages[region.toUpperCase()] : undefined;
  const formId = `conflict-${item._id}`;

  const renderField = (attribute: ConflictAttribute) => {
    const { field_type, field_name, field_display_name } =
      attribute.attribute_id;
    const value = values[field_name] ?? '';
    const placeholder = attribute.populate ? String(attribute.populate) : '';
    const suggestion = attribute.populate ? (
      <span className="sk:inline-flex sk:items-center sk:gap-1 sk:text-xs sk:text-fg-tertiary">
        Suggestion source
        <Tooltip
          content={`If UPC is marked as not applicable, we use the ${alternateIdentifier} as the unique identifier for your item. This is so we can establish a globally unique identifier for this item.`}
        >
          <InfoIcon size={12} aria-label="About the suggestion" />
        </Tooltip>
      </span>
    ) : null;

    if (field_type === 'text' && field_name === 'short_title') {
      return (
        <FormItem
          key={attribute._id}
          label={
            <span className="sk:flex sk:w-full sk:justify-between sk:gap-2">
              {field_display_name}
              <span>{`${value.length}/${SHORT_TITLE_MAX}`}</span>
            </span>
          }
          error={errors[field_name]}
        >
          <TextAreaField
            label={field_display_name}
            rows={2}
            maxLength={SHORT_TITLE_MAX}
            placeholder={placeholder}
            value={value}
            onChange={next => change(field_name, next)}
            isDisabled={isReadOnly}
            isInvalid={!!errors[field_name]}
          />
          {suggestion}
        </FormItem>
      );
    }

    if (field_type !== 'text' && field_type !== 'number') {
      return null;
    }

    return (
      <FormItem
        key={attribute._id}
        label={
          field_display_name === 'upc/gtin' ? 'UPC / GTIN' : field_display_name
        }
        hint={hintFor(field_display_name)}
        error={errors[field_name]}
      >
        {({ labelId }) => (
          <>
            <TextField
              size="sm"
              aria-labelledby={labelId}
              type={field_type === 'number' ? 'number' : 'text'}
              placeholder={placeholder}
              value={value}
              onChange={next => change(field_name, next)}
              isDisabled={isReadOnly}
              isInvalid={!!errors[field_name]}
              className="sk:w-full"
            />
            {suggestion}
          </>
        )}
      </FormItem>
    );
  };

  return (
    <Row id={item._id}>
      <Cell>
        <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3">
          <Avatar
            src={item.p_images?.display_image}
            name={item.short_title || item.p_title}
            shape="square"
            size="md"
          />
          <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
            <a
              href={`/app/catalogs/catalog/${encodeURIComponent(item._id)}`}
              target="_blank"
              rel="noreferrer"
              className="sk:truncate sk:text-sm sk:text-link sk:no-underline sk:hover:underline"
            >
              {item.short_title || item.p_title}
            </a>
            <span className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
              {item.p_sku ? `SKU ${item.p_sku}` : null}
              {DISCONTINUED.includes(item.state?.toLowerCase() ?? '') ? (
                <Badge>Discontinued</Badge>
              ) : null}
            </span>
          </div>
        </div>
      </Cell>
      <Cell>
        <form
          id={formId}
          autoComplete="off"
          noValidate
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            submit(false);
          }}
          className="sk:flex sk:flex-col sk:gap-3"
        >
          {(item.conflictAttributes ?? []).map(renderField)}
        </form>
      </Cell>
      <Cell>
        <MarketplaceIcons
          marketplaces={item.marketplaces}
          readOnly
          onToggleSync={() => undefined}
        />
      </Cell>
      <Cell>
        {region ? (
          <span className="sk:inline-flex sk:items-center sk:gap-1.5 sk:text-dense sk:text-fg-secondary">
            {flag ? (
              <img
                src={flag}
                alt=""
                className="sk:h-3 sk:w-4 sk:rounded-xs sk:object-cover"
              />
            ) : null}
            {region}
          </span>
        ) : (
          <span className="sk:text-fg-tertiary">—</span>
        )}
      </Cell>
      <Cell>
        {isActionsHidden ? null : (
          <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
            <Tooltip
              content={
                item.NA ? conflictHelpText.remove : conflictHelpText.ignore
              }
            >
              <Button
                variant="pill"
                size="sm"
                isDisabled={!item.NA || isReadOnly || !!pending}
                onPress={() => submit(true)}
              >
                {pending === 'ignore' ? 'Ignoring…' : 'Ignore'}
              </Button>
            </Tooltip>
            <Tooltip content={conflictHelpText.resolve}>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                form={formId}
                isDisabled={isReadOnly || !!pending}
              >
                {pending === 'resolve' ? 'Resolving…' : 'Resolve'}
              </Button>
            </Tooltip>
          </div>
        )}
      </Cell>
    </Row>
  );
}
